
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from "./default";
import {  CustomMessagesResponse, CustomMessage, MessageConditionsResponse, AutoMessagesListResponse, AutoMessage, AutoMessageInstallResponse,StoredMessageTemplate, MessageTemplate, SystemTemplatesResponse, StoredTemplatesResponse, Preview } from "./types/messages";

type TagType = 'SystemMessage'| 'CustomMessage' | 'TemplateMessage' | 'MessageCondition' | 'AutoMessage';

const SystemMessageTag: TagType = 'SystemMessage';
const TemplateMessageTag: TagType = 'TemplateMessage';
const CustomMessageTag: TagType = 'CustomMessage';
const MessageConditionTag : TagType = 'MessageCondition';
const AutoMessageTag : TagType = 'AutoMessage';

interface PreviewRequest {
  content: string
  language: string
}

// Define a service using a base URL and expected endpoints
export const MessageApi = createApi({
  reducerPath:'apiMessage',
  baseQuery: baseQuery,
  tagTypes: [CustomMessageTag, TemplateMessageTag, MessageConditionTag, AutoMessageTag, SystemMessageTag],
  endpoints: (builder) => ({
    getSystemTemplates:  builder.query<SystemTemplatesResponse, void>({
      query: () => `api/messages/system`,
      providesTags: (result, error, arg) =>
      result
        ? [...result.templates.map(({ id }) => ({ type: SystemMessageTag, id })), SystemMessageTag]
        : [SystemMessageTag],
    }),
    getAllTemplates:  builder.query<StoredTemplatesResponse, string|undefined>({
      query: (collection?) => {
        var u = `api/messages/templates/`
        if(collection) {
          u += '?collection='+collection
        }
        return u
      },
      providesTags: (result, error, arg) =>
      result?.templates
        ? [...result.templates.map(({ id }) => ({ type: TemplateMessageTag, id })), TemplateMessageTag]
        : [TemplateMessageTag],
    }),
    getAllAutoMessages:  builder.query<AutoMessagesListResponse, void>({
      query: () => `api/messages/automessages`,
      providesTags: (result, error, arg) =>
      (result && result.autoMessages)
        ? [...result.autoMessages.map(({ id }) => ({ type: AutoMessageTag, id })), AutoMessageTag]
        : [AutoMessageTag],
    }),
    getAllCustoms:  builder.query<CustomMessagesResponse, void>({
      query: () => `api/messages/customs.json/`,
      providesTags: (result, error, arg) => {
        return result
          ? [...result.map(({ id }) => ({ type: CustomMessageTag, id })), CustomMessageTag, {type: CustomMessageTag, id: 'LIST'}]
          : [{'type': CustomMessageTag, 'id':'LIST'}]
      }
    }),
    getAllConditions:  builder.query<MessageConditionsResponse, void>({
      query: () => `api/messages/conditions/`,
      providesTags: (result, error, arg) => {
        return result
          ? [...result.map(({ id }) => ({ type: MessageConditionTag, id })), MessageConditionTag, {type: MessageConditionTag, id: 'LIST'}]
          : [{'type': MessageConditionTag, 'id':'LIST'}]
      }
    }),
    getTemplate:  builder.query<MessageTemplate, number>({
      query: (id) => `api/messages/templates/${id}`,
      providesTags: (result, error, arg) => [{type:  TemplateMessageTag, id:arg}]
    }),
    addTemplate: builder.mutation<MessageTemplate, StoredMessageTemplate>({
      query:(body) =>{
        return {
          url: `api/messages/templates/`,
          method: 'POST',
          body: body
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: TemplateMessageTag, id: result ? result.id : '' }, { type: TemplateMessageTag, id:'LIST'}],
    }),
    sendCustom: builder.mutation<CustomMessage, Partial<CustomMessage>>({
      query:(body) =>{
        return {
          url: `api/messages/send`,
          method: 'POST',
          body: body
        }
      },
    }),
    updateTemplate: builder.mutation<MessageTemplate, StoredMessageTemplate>({
      query:(body) =>{
        const  {id, ...data} = body;
        return {
          url: `api/messages/templates/${id}/`,
          method: 'PUT',
          body: data
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: TemplateMessageTag, id: arg.id }, { type: TemplateMessageTag, id:'LIST'} ],
    }),
    deleteTemplate: builder.mutation<MessageTemplate, Partial<MessageTemplate>>({
      query:(m)=>{
        return {
          url: `api/messages/templates/${m.id}/`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: TemplateMessageTag, id: arg.id}, { type: TemplateMessageTag, id:'LIST'} ],
    }),
    preview: builder.query<Preview, PreviewRequest>({
      query:(body) =>{
        return {
          url: `api/messages/preview`,
          method: 'POST',
          body: body,
        }
      },
    }),
    installAutomessage: builder.mutation<AutoMessageInstallResponse, AutoMessage>({
      query:(msg) =>{
        return {
          url: `api/messages/automessages`,
          method: 'POST',
          body: {
            'automessage': msg
          }
        }
      },
      invalidatesTags: (result, error, arg) => {
        if(result?.automessage) {
          const m = result.automessage;
          return [{ type: AutoMessageTag, id: m.id }, { type: AutoMessageTag, id:'LIST'}]
        }
        return [];
      }
    }),
  }),
  
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const { 
  useGetSystemTemplatesQuery,
  useGetAllTemplatesQuery, 
  useGetAllCustomsQuery, useAddTemplateMutation, 
  useGetTemplateQuery, useUpdateTemplateMutation, useDeleteTemplateMutation,
  useGetAllConditionsQuery,
  useSendCustomMutation,
  useGetAllAutoMessagesQuery,
  usePreviewQuery,
  useInstallAutomessageMutation,
} = MessageApi;
